import { useEffect } from 'react';
import './error-toast.css';

/**
 * Props para ErrorToast
 */
interface ErrorToastProps {
  /**
   * Mensaje de error a mostrar
   */
  message: string | null;

  /**
   * Callback cuando el toast se cierra automáticamente
   */
  onClose: () => void;

  /**
   * Duración en ms antes de auto-cerrar
   * @default 4000
   */
  duration?: number;
}

/**
 * ErrorToast - Notificación de error que emerge desde arriba
 *
 * @version 2.0
 * @updated 2026-02-12
 * @module core/projects/components
 *
 * @description
 * Toast de error compacto que emerge desde el centro superior
 * con animación de entrada/salida vertical.
 *
 * @remarks
 * **Características**:
 * - Posicionamiento fijo centrado (position: fixed, left: 50%)
 * - Animación slideDown al aparecer
 * - Animación slideUp al desaparecer
 * - Auto-cierre después de 4s (configurable)
 * - Glassmorphism en estilo de error
 * - Compacto: max-width 400px
 * - Sin emojis (cumple manifiesto)
 *
 * **Cumple con**:
 * - P1: Componente <100 líneas
 * - P3: Documentación JSDoc completa
 * - P6: Variables CSS (no hardcoded)
 * - Manifiesto: Sin emojis decorativos
 *
 * @example
 * ```tsx
 * <ErrorToast
 *   message="La contraseña debe tener mínimo 8 caracteres"
 *   onClose={() => setError(null)}
 * />
 * ```
 */
export function ErrorToast({
  message,
  onClose,
  duration = 4000,
}: ErrorToastProps): JSX.Element | null {
  // Auto-cerrar después de la duración especificada
  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [message, duration, onClose]);

  if (!message) return null;

  return (
    <div className="error-toast" role="alert">
      <div className="error-toast-content">
        <span className="error-toast-message">{message}</span>
        <button
          className="error-toast-close"
          onClick={onClose}
          aria-label="Cerrar notificación"
          type="button"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
